import request, { AxiosResponse } from "axios";
import { ITeamMember } from "encharge-domain/definitions/TeamMember";
import { apiBase } from "../../constants";

/** An Error that keeps the API's errorCode and public data payload - the
 * MFA flow needs both (10090 carries the challenge token in data). */
export class LoginError extends Error {
  errorCode?: number | string;
  data?: Record<string, any>;
  constructor(message: string, errorCode?: number | string, data?: any) {
    super(message);
    this.errorCode = errorCode;
    this.data = data;
  }
}

const rethrowLoginError = (e: any): never => {
  const apiError = e?.response?.data?.error;
  throw new LoginError(
    apiError?.message ||
      e.message ||
      "There was an error while logging you in.",
    apiError?.errorCode,
    apiError?.data
  );
};

export const sendLogin = async (email: string, password: string) => {
  try {
    const res: AxiosResponse<ITeamMember> = await request({
      url: `${apiBase}/v1/accounts/login/`,
      method: "POST",
      data: {
        email,
        password,
      },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    return rethrowLoginError(e);
  }
};

export const sendGoogleLogin = async (idToken: string) => {
  try {
    const res: AxiosResponse<ITeamMember> = await request({
      url: `${apiBase}/v1/accounts/login/google`,
      method: "POST",
      data: {
        idToken,
      },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    return rethrowLoginError(e);
  }
};

/**
 * Second step of an MFA login: the challenge token from the 10090 response
 * plus a TOTP / recovery / email code.
 */
export const sendMfaLogin = async (options: {
  challengeToken: string;
  code: string;
  method?: "totp" | "recovery" | "email";
  rememberDevice?: boolean;
}) => {
  try {
    const res: AxiosResponse<ITeamMember> = await request({
      url: `${apiBase}/v1/accounts/login/mfa`,
      method: "POST",
      data: options,
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    return rethrowLoginError(e);
  }
};

/** Ask for the email fallback code of a pending MFA challenge. */
export const sendMfaEmailCode = async (challengeToken: string) => {
  try {
    await request({
      url: `${apiBase}/v1/accounts/login/mfa/email-code`,
      method: "POST",
      data: { challengeToken },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    return rethrowLoginError(e);
  }
};

/**
 * Checks if the current user is logged in.
 *
 */
export const checkLogin = async () => {
  try {
    await request({
      url: `${apiBase}/v1/heartbeat/authenticated`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "There was an error while logging you in.";
    e.message = msg;
    throw e;
  }
};

/**
 * Request password reset
 *
 */
export const requestResetPassword = async (email: string) => {
  try {
    await request({
      url: `${apiBase}/v1/accounts/request-reset-password`,
      method: "POST",
      data: {
        email,
      },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message || e.message || "Unknown error";
    e.message = msg;
    throw e;
  }
};

/**
 * Reset password
 *
 */
export const resetPassword = async (data: {
  password: string;
  token: string;
}) => {
  try {
    await request({
      url: `${apiBase}/v1/accounts/reset-password`,
      method: "POST",
      data,
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    // If we have a specific error message from server throw it.
    const msg =
      e?.response?.data?.error?.message || e.message || "Unknown error";
    e.message = msg;
    throw e;
  }
};
